import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as moment from 'moment/moment';
import { RentReservationService } from '../../../core/services/rent-reservation.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AppUtils } from '../../../core/config/app.utils';
import { ReportsService } from '../../../core/services/reports.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TransferReservationService } from '../../../core/services/transfer-reservation.service';

@Component({
  selector: 'app-multiple-paging-board-popup',
  templateUrl: './multiple-paging-board-popup.component.html',
  styleUrls: ['./multiple-paging-board-popup.component.scss'],
})
export class MultiplePagingBoardPopupComponent implements OnInit {
  @Output() multiplePagingBoardOnChange: EventEmitter<any> = new EventEmitter();
  @Input() isVisible = false;
  @Input() tourType = '';

  dateRanges: any;
  bookings: any[] = [];
  pagingBoardForm: FormGroup | any;
  pagingBoardErrors: any;
  loading = false;
  visibleEditFormPopup = false;
  selectedRowIndex: any;
  pdfLoading = false;
  pagingBoardPdf: any;
  isEditData = false;
  skeletonTableColumns: any[] = [
    'Customer Name',
    'Vehicle Category',
    'Arrival Date',
    'Arrival Time',
    'Arrival Flight Number',
    'Drop Off Location',
    'Number of Pax',
    'Contact Number',
    'Agent Name',
    'Action',
  ];

  constructor(
    private rentReservationService: RentReservationService,
    private fb: FormBuilder,
    private reportService: ReportsService,
    private message: NzMessageService,
    private transferService: TransferReservationService,
  ) {}

  ngOnInit() {
    this.initPagingBoardForm();
  }

  initPagingBoardForm() {
    this.pagingBoardForm = this.fb.group({
      customer: [null, [Validators.required, this.characterValidator]],
      date: [null, [Validators.required]],
      time: [null, [Validators.required]],
      flight: [null, [Validators.required]],
      dropLocation: [null],
      numOfPax: [null],
      vehicleCategory: [{ value: null, disabled: true }],
      customerNumber: [
        null,
        [
          Validators.required,
          Validators.maxLength(15),
          Validators.pattern(/^\d+$/),
        ],
      ],
      agent: [{ value: null, disabled: true }],
    });
    this.pagingBoardErrors = {
      customer: {},
      date: {},
      time: {},
      flight: {},
      dropLocation: {},
      numOfPax: {},
      vehicleCategory: {},
      customerNumber: {},
      agent: {},
    };
    this.pagingBoardForm.valueChanges.subscribe(() => {
      this.pagingBoardErrors = AppUtils.getFormErrors(
        this.pagingBoardForm,
        this.pagingBoardErrors,
      );
    });
  }
  characterValidator = (control: FormControl) => {
    const value = control.value;
    if (!value) {
      return { required: true };
    }
    const words = value.split(' ');
    if (words.length > 2 || words.find((v: string) => v.length > 15)) {
      return { wordLimit: true };
    }
    return {};
  };

  onChangeDatePicker = async (result: Date[]) => {
    if (result[0] && result[1]) {
      this.loading = true;
      const startDate = moment(result[0]).startOf('day').format();
      const endDate = moment(result[1]).endOf('day').format();
      if (this.tourType === 'RENT') {
        this.rentReservationService
          .getAllPagingBoardDataReservations({ startDate, endDate })
          .subscribe(
            (res: any) => {
              const bookingData = res;
              this.bookings = bookingData.map((val: any) => {
                return {
                  ...val,
                  date: val.actualStartDateAndTime
                    ? val.actualStartDateAndTime
                    : val.startDateAndTime,
                  time: val.actualStartDateAndTime
                    ? val.actualStartDateAndTime
                    : val.startDateAndTime,
                  type: val.type ? val.type : 'Arrival',
                };
              });
              this.isEditData = true;
              this.loading = false;
            },
            (error) => {
              this.loading = false;
            },
          );
      } else if (this.tourType === 'TRANSFER') {
        this.transferService
          .getAllPagingBoardDataTransfer({ startDate, endDate })
          .subscribe(
            (res: any) => {
              const bookingData = res;
              this.bookings = bookingData.map((val: any) => {
                return {
                  ...val,
                  date: val.startDateAndTime,
                  time: val.startDateAndTime,
                  type: val.type ? val.type : 'Arrival',
                };
              });
              this.isEditData = true;
              this.loading = false;
            },
            (error) => {
              this.loading = false;
            },
          );
      }
    }
  };

  editDetails(data: any, i: number): void {
    this.selectedRowIndex = i;
    this.pagingBoardForm.patchValue(data);
    this.pagingBoardForm.get('date').patchValue(new Date(data.date));
    this.pagingBoardForm.get('time').patchValue(new Date(data.time));
    this.visibleEditFormPopup = true;
  }
  cancelPagingBoardEditModal() {
    this.visibleEditFormPopup = false;
    this.selectedRowIndex = null;
    this.pagingBoardForm.reset();
  }
  saveUpdatedData() {
    const bookingData = this.bookings;
    this.bookings = [];
    bookingData[this.selectedRowIndex] = {
      ...bookingData[this.selectedRowIndex],
      ...this.pagingBoardForm.getRawValue(),
    };
    this.visibleEditFormPopup = false;
    this.selectedRowIndex = null;
    this.pagingBoardForm.reset();
    bookingData.forEach((cal: any) => {
      this.bookings.push(cal);
    });
    this.isEditData = true;
  }

  async loadPagingBoard(): Promise<void> {
    try {
      let payload = this.bookings;
      payload = payload.map((val: any) => {
        return {
          ...val,
          date: moment(val.date).format('MMM Do YYYY').toString(),
          time: moment(val.time).format('LT').toString(),
          agent: val.agent ? val.agent : '-',
        };
      });
      this.pdfLoading = true;
      this.pagingBoardPdf =
        await this.reportService.generatePagingBoardReport(payload);
      this.message.create('success', `PDF has been created!`);
      this.isEditData = false;
      this.pdfLoading = false;
    } catch (e) {
      this.message.create('error', `Can't generate PDF`);
    }
  }

  pagingBoardAction(type: string) {
    switch (type) {
      case 'preview':
        this.reportService.viewReport(this.pagingBoardPdf);
        break;
      case 'download':
        this.reportService.downloadReport(this.pagingBoardPdf);
        break;
      case 'email':
        this.reportService.emailPdf();
        break;
      case 'print':
        this.reportService.printReport(this.pagingBoardPdf);
        break;
    }
  }

  handleCancel(): void {
    this.isVisible = false;
    this.bookings = [];
    this.multiplePagingBoardOnChange.emit({
      name: 'isVisible',
      value: false,
    });
  }
}
